<template>
  <div class="trials-eoi">
    <div class="trials-eoi__card">
      <div class="trials-eoi__header">
        <TenantLogo class="trials-eoi__logo" />
        <h1>{{ clubName }}</h1>
        <p class="muted">{{ depositMode ? "Trial deposit" : "Trials — register your interest" }}</p>
      </div>

      <div v-if="loading" class="muted center">Loading…</div>

      <!-- "Pay your deposit" page, reached from the signed link in the offer email -->
      <div v-else-if="depositMode" class="trials-eoi__deposit">
        <p v-if="error" class="error-text">{{ error }}</p>
        <template v-else-if="deposit.status === 'paid'">
          <div class="trials-eoi__success">
            <h2><i class="bi bi-check-circle"></i> Deposit received</h2>
            <p>
              Thanks — <strong>{{ deposit.playerName }}</strong>'s
              {{ deposit.amountLabel }} deposit has been paid.
            </p>
          </div>
          <p v-if="!deposit.joined" class="next-step">
            Next, set up {{ deposit.playerName }}'s account on the club app so they get
            fixtures, training times and team messages:
          </p>
          <a v-if="!deposit.joined" :href="deposit.registerLink" class="button-link">
            Register for the {{ clubName }} app
          </a>
        </template>
        <template v-else>
          <h2>Pay {{ deposit.playerName }}'s deposit</h2>
          <p>
            {{ clubName }} has offered <strong>{{ deposit.playerName }}</strong> a place
            <template v-if="deposit.team">in the <strong>{{ deposit.team }}</strong></template>.
            A non-refundable deposit secures it.
          </p>
          <div class="amount">{{ deposit.amountLabel }}</div>
          <p v-if="cancelled" class="muted center">Payment was cancelled — you can try again below.</p>
          <button type="button" :disabled="busy" @click="payDeposit">
            {{ busy ? "Taking you to secure checkout…" : "Pay by card" }}
          </button>
          <p class="muted center small">Card payments are processed securely by Stripe.</p>
        </template>
      </div>

      <div v-else-if="!open" class="trials-eoi__closed">
        <i class="bi bi-calendar-x"></i>
        <p>Trials aren't open at the moment.</p>
        <p class="muted">Keep an eye on the club's page and socials for the next trial dates.</p>
      </div>

      <div v-else-if="submitted" class="trials-eoi__success">
        <h2><i class="bi bi-check-circle"></i> You're registered</h2>
        <p>
          Thanks — <strong>{{ submittedName }}</strong>'s interest has been sent
          to {{ clubName }}. We look forward to seeing you at trials.
        </p>
      </div>

      <form v-else @submit.prevent="submit" class="trials-eoi__form">
        <!-- Info blocks (collapsible) -->
        <details v-if="config.info.trials" class="info-block" open>
          <summary><i class="bi bi-info-circle"></i> Trial information</summary>
          <p class="info-text">{{ config.info.trials }}</p>
        </details>

        <label>Player's full name *
          <input v-model="form.player_name" type="text" required placeholder="Player name" />
        </label>

        <div class="row">
          <label>Date of birth
            <input v-model="form.dob" type="date" />
          </label>
          <label v-if="config.ageGroups.length">Age group *
            <select v-model="form.age_group" required>
              <option value="">Select…</option>
              <option v-for="a in config.ageGroups" :key="a" :value="a">{{ a }}</option>
            </select>
          </label>
        </div>

        <!-- Which trial(s) — filtered to the chosen age group -->
        <div v-if="form.age_group && relevantSessions.length" class="sessions">
          <span class="block-label">Which trial(s) will you attend? *</span>
          <label v-for="s in relevantSessions" :key="s.label" class="session-check">
            <input type="checkbox" :value="s.label" v-model="form.sessions_attending" />
            <span>
              <strong>{{ s.label }}</strong> — {{ formatDate(s.date) }}<span v-if="s.time"> · {{ formatTime(s.time) }}</span>
              <span v-if="s.venue"> · {{ s.venue }}</span>
              <span v-if="s.description" class="session-desc">{{ s.description }}</span>
            </span>
          </label>
        </div>

        <label>Preferred position
          <input v-model="form.preferred_position" type="text" placeholder="e.g. Midfielder" />
        </label>

        <!-- Gated on the club's `fields.experience` config flag (defaults to
             on) — both values are stored on trial_registrations and appear in
             the admin CSV export. -->
        <label v-if="config.fields.experience">Playing experience
          <textarea v-model="form.experience" rows="2"
            placeholder="Recent clubs, years playing, positions"></textarea>
        </label>
        <label v-if="config.fields.experience">Previous club / school (if any)
          <input v-model="form.previous_club" type="text" />
        </label>

        <hr />

        <label>Parent / caregiver name
          <input v-model="form.parent_name" type="text" />
        </label>
        <div class="row">
          <label>Contact number
            <input v-model="form.parent_contact" type="tel" placeholder="Mobile" />
          </label>
          <label>Email *
            <input v-model="form.email" type="email" required placeholder="you@example.com" />
          </label>
        </div>

        <!-- Fees info (read-only; no payment taken at the trial stage) -->
        <details v-if="config.info.fees" class="info-block">
          <summary><i class="bi bi-cash-coin"></i> Fees</summary>
          <p class="info-text">{{ config.info.fees }}</p>
        </details>

        <!-- Codes of conduct — read before agreeing to the acknowledgments -->
        <div v-if="codesOfConduct.length" class="codes">
          <span class="block-label"><i class="bi bi-file-earmark-text"></i> Codes of conduct</span>
          <p class="codes-note">Please read these before attending:</p>
          <a
            v-for="doc in codesOfConduct"
            :key="doc.url"
            :href="doc.url"
            target="_blank"
            rel="noopener"
            class="code-link"
          >
            <i class="bi bi-box-arrow-up-right"></i> {{ doc.label || doc.url }}
          </a>
        </div>

        <div v-if="config.acknowledgments.length" class="acks">
          <label v-for="(ack, i) in config.acknowledgments" :key="i" class="check">
            <input type="checkbox" v-model="acksChecked[i]" /> {{ ack }} <span class="req">*</span>
          </label>
        </div>

        <button type="submit" :disabled="busy || !canSubmit">
          {{ busy ? "Submitting…" : "Register interest" }}
        </button>
        <p v-if="error" class="error-text">{{ error }}</p>
      </form>
    </div>
  </div>
</template>

<script>
import { Trials } from "@/services/trials-service.js";
import { StripeConnect } from "@/services/stripe-connect-service.js";
import TenantLogo from "@/components/TenantLogo/index.vue";

export default {
  name: "TrialsRegister",
  components: { TenantLogo },
  data() {
    return {
      clubId: "",
      clubName: "",
      open: false,
      loading: true,
      config: {
        info: { trials: "", fees: "" },
        sessions: [],
        ageGroups: [],
        fields: {},
        deposit: { enabled: false, bands: [] },
        acknowledgments: [],
        codesOfConduct: [],
      },
      form: {
        player_name: "",
        dob: "",
        age_group: "",
        preferred_position: "",
        experience: "",
        previous_club: "",
        parent_name: "",
        parent_contact: "",
        email: "",
        sessions_attending: [],
      },
      acksChecked: [],
      busy: false,
      error: "",
      submitted: false,
      submittedName: "",
      // Deposit page state (?pay=<id>&t=<sig>, &deposit=<session|cancelled> on return)
      depositMode: false,
      deposit: {},
      cancelled: false,
    };
  },
  computed: {
    relevantSessions() {
      return (this.config.sessions || []).filter(
        (s) => !s.ageGroups?.length || s.ageGroups.includes(this.form.age_group),
      );
    },
    codesOfConduct() {
      return (this.config.codesOfConduct || []).filter((d) => d && d.url);
    },
    allAcksChecked() {
      return this.config.acknowledgments.every((_, i) => this.acksChecked[i]);
    },
    canSubmit() {
      const needSession = this.form.age_group && this.relevantSessions.length;
      return (
        this.allAcksChecked &&
        (!needSession || this.form.sessions_attending.length > 0)
      );
    },
  },
  async created() {
    this.clubId = this.$route.params.clubId;
    if (this.$route.query.pay) {
      await this.loadDeposit();
      return;
    }
    try {
      const res = await Trials.getPublicConfig(this.clubId);
      this.open = res.data.open;
      this.clubName = res.data.clubName;
      this.config = res.data.config;
      this.acksChecked = this.config.acknowledgments.map(() => false);
    } catch (e) {
      this.error = "Couldn't load the trials form.";
    } finally {
      this.loading = false;
    }
  },
  watch: {
    // Reset chosen sessions when the age group changes (different set applies).
    "form.age_group"() {
      this.form.sessions_attending = [];
    },
  },
  methods: {
    /**
     * Deposit page. Coming back from Stripe we confirm the session first so
     * the family sees "paid" even if the webhook hasn't landed yet.
     */
    async loadDeposit() {
      this.depositMode = true;
      const { pay, t, deposit } = this.$route.query;
      this.cancelled = deposit === "cancelled";
      try {
        if (deposit && deposit !== "cancelled") {
          try {
            await StripeConnect.confirmCheckoutSession(this.clubId, deposit);
          } catch (e) {
            // Not fatal — the status below comes from the row either way.
          }
        }
        const res = await Trials.getDeposit(this.clubId, pay, t);
        this.deposit = res.data;
        this.clubName = res.data.clubName;
        if (!res.data.amountPence && res.data.status !== "paid") {
          this.error = "No deposit is due for this registration.";
        }
      } catch (e) {
        this.error = e?.response?.data?.error || "This deposit link is not valid.";
      } finally {
        this.loading = false;
      }
    },
    async payDeposit() {
      this.busy = true;
      this.error = "";
      try {
        const { pay, t } = this.$route.query;
        const res = await StripeConnect.createTrialDepositCheckout(this.clubId, pay, t);
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        this.error = res.data?.error || "Could not start the payment.";
      } catch (e) {
        this.error = e?.response?.data?.error || "Could not start the payment.";
      }
      this.busy = false;
    },
    formatDate(iso) {
      if (!iso) return "";
      return new Date(iso).toLocaleDateString("en-AU", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    },
    formatTime(t) {
      if (!t) return "";
      const [h, m] = t.split(":");
      const d = new Date();
      d.setHours(+h, +m);
      return d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
    },
    async submit() {
      this.error = "";
      if (!this.canSubmit) {
        this.error = "Please pick your trial(s) and tick all the required acknowledgments.";
        return;
      }
      this.busy = true;
      try {
        const res = await Trials.submit({
          clubId: this.clubId,
          ...this.form,
          acknowledgments: this.config.acknowledgments,
        });
        this.submittedName = this.form.player_name;
        this.submitted = true;
        window.scrollTo(0, 0);
      } catch (e) {
        this.error =
          e?.response?.data?.error || "Something went wrong — please try again.";
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.trials-eoi {
  min-height: 100vh;
  background: #f5f6f8;
  display: flex;
  justify-content: center;
  padding: 40px 16px;

  &__card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #e9ecef;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
    padding: 32px;
    width: 100%;
    max-width: 580px;
    height: fit-content;
  }

  &__header {
    text-align: center;
    margin-bottom: 20px;

    h1 { font-size: 1.3rem; margin: 10px 0 2px; }
  }

  &__logo { width: 64px; height: 64px; margin: 0 auto; }

  &__closed, &__success {
    text-align: center;
    padding: 20px 0;

    i { font-size: 2rem; color: #6c757d; }
  }

  &__success h2 { color: #1e7b3c; }

  &__deposit {
    display: flex;
    flex-direction: column;
    gap: 14px;
    text-align: center;

    h2 { font-size: 1.25rem; margin: 0; }

    .amount {
      font-size: 2.2rem;
      font-weight: 800;
      color: #1e7b3c;
      margin: 6px 0;
    }

    .next-step { margin-top: 6px; }

    button, .button-link {
      display: block;
      padding: 12px;
      border: none;
      border-radius: 8px;
      background: #198754;
      color: #fff;
      font-weight: 700;
      font-size: 1rem;
      cursor: pointer;
      text-decoration: none;

      &:disabled { opacity: 0.6; cursor: default; }
    }

    .small { font-size: 0.8rem; }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 14px;

    label {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-weight: 600;
      font-size: 0.9rem;
    }

    input, select, textarea {
      padding: 10px 12px;
      border: 1px solid #ced4da;
      border-radius: 8px;
      font-weight: 400;
      font-size: 0.95rem;
    }

    .row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;

      @media (max-width: 520px) { grid-template-columns: 1fr; }
    }

    hr { border: none; border-top: 1px solid #eee; margin: 4px 0; }
  }

  .info-block {
    border: 1px solid #e3e9f0;
    border-radius: 8px;
    padding: 6px 12px;
    background: #f8fafc;

    summary {
      cursor: pointer;
      font-weight: 600;
      font-size: 0.9rem;
      padding: 4px 0;

      i { margin-right: 0.35rem; color: #2d6cdf; }
    }

    .info-text {
      white-space: pre-line;
      font-size: 0.85rem;
      color: #445;
      margin: 6px 0;
    }

    .deposit-bands {
      margin: 6px 0 4px;
      padding-left: 1.1rem;
      font-size: 0.85rem;
    }
  }

  .block-label {
    font-weight: 600;
    font-size: 0.9rem;
    display: block;
    margin-bottom: 4px;
  }

  .sessions {
    background: #eef6ff;
    border: 1px solid #cfe2ff;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .session-check {
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
    font-weight: 400;

    input { width: 18px; height: 18px; margin-top: 2px; accent-color: #2d6cdf; }

    .session-desc {
      display: block;
      color: #566;
      font-size: 0.8rem;
      margin-top: 2px;
    }
  }

  .check {
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
    font-weight: 400;

    input { width: 18px; height: 18px; margin-top: 2px; accent-color: #198754; }
    .req { color: #dc3545; }
  }

  .volunteering, .acks {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: #f8f9fa;
    border-radius: 8px;
    padding: 12px;
  }

  .codes {
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: #f8f9fa;
    border-radius: 8px;
    padding: 12px;

    .codes-note { font-size: 0.82rem; color: #566; margin: 0 0 2px; }
  }

  .code-link {
    color: #2d6cdf;
    font-size: 0.88rem;
    text-decoration: none;

    &:hover { text-decoration: underline; }
    i { margin-right: 4px; font-size: 0.8rem; }
  }

  .deposit-note {
    background: #fff8e1;
    border: 1px solid #ffe08a;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 0.88rem;
  }

  button {
    padding: 12px;
    border: none;
    border-radius: 8px;
    background: #198754;
    color: #fff;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;

    &:disabled { opacity: 0.6; cursor: default; }
  }

  .muted {
    color: #6c757d;
    font-size: 0.9rem;
    &.center { text-align: center; }
  }

  .error-text { color: #dc3545; font-size: 0.88rem; }
}
</style>
